export function isRasterImageFile(file: File): boolean {
    return file.type.startsWith('image/');
}

function loadImageElement(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.decoding = 'async';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('LOAD_FAILED'));
        img.src = src;
    });
}

export async function convertImageFileToJpeg(file: File, quality = 0.92): Promise<File> {
    const url = URL.createObjectURL(file);
    try {
        const img = await loadImageElement(url);
        const w = img.naturalWidth || img.width;
        const h = img.naturalHeight || img.height;
        if (!w || !h) {
            throw new Error('INVALID_DIMENSIONS');
        }

        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            throw new Error('NO_CONTEXT');
        }

        const mime = file.type.toLowerCase();
        if (mime !== 'image/jpeg' && mime !== 'image/jpg') {
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, w, h);
        }
        ctx.drawImage(img, 0, 0);

        const blob = await new Promise<Blob>((resolve, reject) => {
            canvas.toBlob(b => (b ? resolve(b) : reject(new Error('TO_BLOB'))), 'image/jpeg', quality);
        });

        const baseName = file.name.replace(/\.[^/.]+$/, '') || 'image';
        return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
    } finally {
        URL.revokeObjectURL(url);
    }
}

export function buildMimeExtensionChecker(imageTypes: string[]): (file: File) => boolean {
    const types = imageTypes.map(t => t.toLowerCase());
    return (file: File) => {
        const mimePart = (file.type.split('/')[1] ?? '').toLowerCase();
        if (mimePart && types.includes(mimePart)) {
            return true;
        }
        const name = file.name.toLowerCase();
        return types.some(ext => {
            const e = ext.toLowerCase();
            if (e === 'jpeg') {
                return name.endsWith('.jpeg') || name.endsWith('.jpg');
            }
            return name.endsWith(`.${e}`);
        });
    };
}

export function isPhotosGalleryFormatAllowed(file: File): boolean {
    const mime = file.type.toLowerCase();
    const name = file.name.toLowerCase();
    if (mime === '' && name === '' && file.size > 0) {
        return true;
    }

    const mimeOk =
        mime === 'image/jpeg' ||
        mime === 'image/jpg' ||
        mime === 'image/png' ||
        mime === 'image/pjpeg' ||
        mime === 'image/heic' ||
        mime === 'image/heif';

    const extOk =
        name.endsWith('.jpg') ||
        name.endsWith('.jpeg') ||
        name.endsWith('.png') ||
        name.endsWith('.heic') ||
        name.endsWith('.heif');

    return mimeOk || extOk;
}

export function isStandardProfileImageFormatAllowed(file: File): boolean {
    return isPhotosGalleryFormatAllowed(file);
}

export function isJpgPngOnlyFormatAllowed(file: File): boolean {
    const mime = file.type.toLowerCase();
    const name = file.name.toLowerCase();
    const mimeOk = mime === 'image/jpeg' || mime === 'image/jpg' || mime === 'image/png' || mime === 'image/pjpeg';
    const extOk = name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png');
    return mimeOk || extOk;
}

export function isVaccineRecordFileAllowed(file: File): boolean {
    const mime = file.type.toLowerCase();
    const name = file.name.toLowerCase();
    if (mime === 'application/pdf' || name.endsWith('.pdf')) {
        return true;
    }
    return isJpgPngOnlyFormatAllowed(file);
}

export async function normalizeImageToJpegIfFormatNotAllowed(
    file: File,
    isAllowed: (f: File) => boolean
): Promise<File> {
    if (isAllowed(file)) {
        return file;
    }
    if (!isRasterImageFile(file)) {
        throw new Error('NOT_RASTER_IMAGE');
    }
    const converted = await convertImageFileToJpeg(file);
    if (!isAllowed(converted)) {
        throw new Error('CONVERSION_INCOMPATIBLE_WITH_RULES');
    }
    return converted;
}
